import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import { HSpacer, VSpacer } from '@nx-smartmonkey/ui';
import { useState } from 'react';

import { Grid } from '@mui/material';
import { InfoContainer } from '../../../../../components/right-panel-info/RightPanel.styled';
import { Project } from '../../../../../domain/project/Project';
import { Supervisor } from '../../../../../domain/supervisor/Supervisor';
import {
  SupervisorFieldTypes,
  SupervisorStopFieldType,
  SupervisorStopSurveyType,
  supervisorStopSurveyValues,
} from '../../../../../domain/supervisor/SupervisorFields';
import { intl } from '../../../../../helpers/IntGlobalProvider';
import { useUpdateSupervisor } from '../../../../../hooks/supervisor/useUpdateSupervisor';
import { SupervisorMapper } from '../../../../../mappers/supervisor/SupervisorMapper';
import Button from '../../../../../react-components/Button';
import Text from '../../../../../react-components/Text';
import { AppField, ManageFieldsDialog } from './ManageFieldsDialog';

interface SupervisorStopSurveyFieldsProps {
  supervisor: Supervisor;
  project: Project;
}

export const SupervisorStopSurveyFields = ({ supervisor, project }: SupervisorStopSurveyFieldsProps) => {
  const { run: editSupervisor } = useUpdateSupervisor();
  const [manageFieldsDialogOpen, setManageFieldsDialogOpen] = useState<boolean>(false);

  const getAllFields = (): AppField<SupervisorStopSurveyType>[] => {
    const fields: AppField<SupervisorStopFieldType>[] = Object.entries(supervisorStopSurveyValues).map(([id, type]) => {
      const foundField = supervisor.survey_fields.find((f) => f.id === id);

      return {
        id,
        text: intl.formatMessage({ id: `properties.stop.survey_fields.${id}` }),
        enabled: Boolean(foundField),
        type: type as SupervisorFieldTypes,
        is_custom_field: false,
      };
    });
    return fields;
  };

  const getAllActiveFields = (): AppField<SupervisorStopSurveyType>[] => {
    return (
      supervisor.survey_fields
        // Filtering the fields of type "custom field" that are enabled
        .filter((field) => {
          if (!field.is_custom_field) return true;

          const cFieldFound = project.custom_fields?.service_report_completed.find(
            (cField) => cField.custom_id === field.id
          );
          return Boolean(cFieldFound);
        })
        .map((field) => {
          if (!field.is_custom_field) {
            return {
              id: field.id,
              text: intl.formatMessage({ id: `properties.stop.survey_fields.${field.id}` }),
              enabled: true,
              type: field.type,
              is_custom_field: false,
            };
          }

          const cFieldFound = project.custom_fields?.service_report_completed.find(
            (cField) => cField.custom_id === field.id
          );

          return {
            id: cFieldFound!.custom_id,
            text: cFieldFound!.label ?? ``,
            enabled: true,
            type: cFieldFound!.field_type,
            is_custom_field: true,
          };
        })
    );
  };

  return (
    <>
      <InfoContainer>
        <Text
          variant="h6"
          style={{ cursor: `default`, fontWeight: 600 }}
          intlId="supervisors.details.group.survey_fields"
        />

        <VSpacer small />

        <Text variant="label" intlId="supervisors.app.options.survey_fields.info" />

        <VSpacer small />

        {getAllActiveFields().map((field) => {
          return (
            <Grid
              container
              direction="row"
              alignItems="center"
              style={{
                border: `1px solid #e0e0e0`,
                borderRadius: `2px`,
                padding: `8px`,
                margin: `8px 0`,
                height: `42px`,
                backgroundColor: `white`,
              }}
            >
              <Text variant="body">{field.text}</Text>
            </Grid>
          );
        })}

        <Button styleType="secondary" onClick={() => setManageFieldsDialogOpen(true)}>
          <CheckBoxOutlinedIcon />
          <HSpacer small />
          <Text intlId="supervisors.app.options.manage_fields" style={{ color: `white`, cursor: `pointer` }} />
        </Button>
      </InfoContainer>

      <ManageFieldsDialog
        open={manageFieldsDialogOpen}
        onClose={() => setManageFieldsDialogOpen(false)}
        fields={getAllFields()}
        onUpdate={({ field, selected }) => {
          const supervisorToUpdate = SupervisorMapper.toDTO(supervisor);

          if (selected) {
            const { text, enabled, ...rest } = field;
            supervisorToUpdate.survey_fields.push(rest);
          } else {
            supervisorToUpdate.survey_fields = supervisorToUpdate.survey_fields.filter((f) => f.id !== field.id);
          }

          editSupervisor({
            supervisor,
            changes: {
              survey_fields: supervisorToUpdate.survey_fields,
            },
          });
        }}
        onUpdateAll={({ selected }) => {
          const supervisorToUpdate = SupervisorMapper.toDTO(supervisor);

          if (selected) {
            supervisorToUpdate.survey_fields = getAllFields().map((field) => {
              const { text, enabled, ...rest } = field;
              return rest;
            });
          } else {
            supervisorToUpdate.survey_fields = [];
          }

          editSupervisor({
            supervisor,
            changes: {
              survey_fields: supervisorToUpdate.survey_fields,
            },
          });
        }}
      />
    </>
  );
};
